import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  Panel,
  Node,
  Edge,
  BackgroundVariant,
  ControlButton,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Chip, 
  IconButton, 
  Tooltip,
  useTheme,
  alpha,
  Stack,
  Divider,
} from '@mui/material';
import {
  CenterFocusStrong as CenterIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  AccountTree as TreeIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

import { ArvoreIndicacao } from '../../services/indicacaoService';
import { 
  convertToReactFlow, 
  calcularEstatisticasRede, 
  IndicacaoNodeData, 
  IndicacaoEdgeData,
  getCoresNivel,
} from '../../utils/indicacaoFlowUtils';
import IndicacaoRootNode from '../flow/nodes/IndicacaoRootNode';
import IndicacaoClienteNode from '../flow/nodes/IndicacaoClienteNode';
import IndicacaoEdge from '../flow/edges/IndicacaoEdge';

// Tipos de nós customizados
const nodeTypes = {
  indicacaoRoot: IndicacaoRootNode,
  indicacaoNode: IndicacaoClienteNode,
};

// Tipos de edges customizados
const edgeTypes = {
  indicacaoEdge: IndicacaoEdge,
};

interface RedeIndicacoesFlowProps {
  arvoreIndicacoes: ArvoreIndicacao[];
  clienteId: number;
  clienteNome: string;
}

const RedeIndicacoesFlowContent: React.FC<RedeIndicacoesFlowProps> = ({
  arvoreIndicacoes,
  clienteId,
  clienteNome,
}) => {
  const theme = useTheme();
  const { fitView, zoomIn, zoomOut, getViewport } = useReactFlow();
  const flowRef = useRef<HTMLDivElement>(null);

  // Converte dados para formato React Flow
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    return convertToReactFlow(arvoreIndicacoes, clienteId, clienteNome);
  }, [arvoreIndicacoes, clienteId, clienteNome]);

  // Estados do React Flow
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Estatísticas da rede
  const estatisticas = useMemo(() => {
    return calcularEstatisticasRede(arvoreIndicacoes);
  }, [arvoreIndicacoes]);

  // Centralizar visualização
  const handleCenterView = useCallback(() => {
    fitView({ duration: 0, padding: 0.2 });
  }, [fitView]);

  // Função para download da rede (TODO: implementar)
  const handleDownload = useCallback(() => {
    // TODO: Implementar download da visualização
    console.log('Download da rede de indicações');
  }, []);

  // Efeito para centralizar ao carregar
  useEffect(() => {
    const timer = setTimeout(() => {
      handleCenterView();
    }, 100);

    return () => clearTimeout(timer);
  }, [handleCenterView]);

  // Se não há indicações, exibe mensagem
  if (arvoreIndicacoes.length === 0) {
    return (
      <Card sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CardContent sx={{ textAlign: 'center' }}>
          <TreeIcon sx={{ fontSize: 60, color: theme.palette.grey[400], mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nenhuma indicação encontrada
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comece a indicar novos clientes para visualizar sua rede aqui
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ height: '100%' }}>
      {/* Estatísticas da rede */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <GroupIcon color="primary" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {estatisticas.totalClientes}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total de Clientes
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TreeIcon color="success" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {estatisticas.profundidadeMaxima}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Níveis na Rede
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TrendingUpIcon color="warning" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {estatisticas.taxaCrescimento.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Crescimento
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <InfoIcon color="info" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {estatisticas.clientesPorNivel[0]?.quantidade || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Indicações Diretas
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Visualização React Flow */}
      <Paper sx={{ height: 600, position: 'relative' }}>
        <ReactFlow
          ref={flowRef}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          fitView
          fitViewOptions={{
            padding: 0.2,
            includeHiddenNodes: false,
          }}
          attributionPosition="bottom-left"
          proOptions={{ hideAttribution: true }}
          style={{
            background: theme.palette.mode === 'dark' 
              ? alpha(theme.palette.background.default, 0.95)
              : alpha(theme.palette.grey[50], 0.95),
          }}
        >
          {/* Fundo com padrão */}
          <Background 
            variant={BackgroundVariant.Dots} 
            gap={24} 
            size={1}
            color={theme.palette.mode === 'dark' ? '#404040' : '#e0e0e0'}
          />

          {/* Controles */}
          <Controls
            style={{
              backgroundColor: theme.palette.background.paper,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
            }}
          >
            <ControlButton onClick={handleCenterView} title="Centralizar">
              <CenterIcon />
            </ControlButton>
          </Controls>


          
        </ReactFlow>
      </Paper>
    </Box>
  );
};

const RedeIndicacoesFlow: React.FC<RedeIndicacoesFlowProps> = (props) => {
  return (
    <ReactFlowProvider>
      <RedeIndicacoesFlowContent {...props} />
    </ReactFlowProvider>
  );
};

export default RedeIndicacoesFlow; 